import * as React from "react";
import { createContext, useContext } from "react";

import { CheckMarkCircle } from "@/assets/icons";
import { constantsData } from "@/data";
import { generateValidClassNameHandler } from "@/lib/utils";

// Types
type StepperOrientation = "horizontal" | "vertical";

type StepState = "active" | "completed" | "inactive" | "loading";

type StepIndicators = {
    active?: React.ReactNode,
    completed?: React.ReactNode,
    inactive?: React.ReactNode,
    loading?: React.ReactNode,
};

interface StepperContextValue {
    activeStep: number,
    focusFirst: () => void,
    focusLast: () => void,
    focusNext: (currentIdx: number) => void, // eslint-disable-line
    focusPrev: (currentIdx: number) => void, // eslint-disable-line
    indicators: StepIndicators,
    orientation: StepperOrientation,
    registerTrigger: (node: HTMLButtonElement | null) => void, // eslint-disable-line
    setActiveStep: (step: number) => void, // eslint-disable-line
    stepsCount: number,
    triggerNodes: HTMLButtonElement[],
}

interface StepItemContextValue {
    isDisabled: boolean,
    isLoading: boolean,
    state: StepState,
    step: number,
}

const StepperContext = createContext<StepperContextValue | undefined>(undefined);

const StepItemContext = createContext<StepItemContextValue | undefined>(undefined);

const useStepper = () => {
    const ctx = useContext(StepperContext);

    if (!ctx) throw new Error(constantsData.msgs.providers.stepper);

    return ctx;
};

const useStepItem = () => {
    const ctx = useContext(StepItemContext);

    if (!ctx) throw new Error(constantsData.msgs.providers.stepperItem);

    return ctx;
};

interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
    defaultValue?: number,
    indicators?: StepIndicators,
    onValueChange?: (value: number) => void, // eslint-disable-line
    orientation?: StepperOrientation,
    value?: number,
}

const Stepper: React.FC<StepperProps> = ({
    children,
    className,
    defaultValue = 1,
    indicators = {},
    onValueChange,
    orientation = "horizontal",
    value,
    ...props
}) => {
    const [activeStep, setActiveStep] = React.useState(defaultValue);

    const [triggerNodes, setTriggerNodes] = React.useState<HTMLButtonElement[]>([]);

    // Register/unregister triggers
    const registerTrigger = React.useCallback(
        (node: HTMLButtonElement | null) => {
            setTriggerNodes((prev) => {
                if (node && !prev.includes(node)) return [...prev, node];

                if (!node && prev.includes(node!)) return prev.filter((n) => n !== node);

                return prev;
            });
        },
        [],
    );

    const handleSetActiveStep = React.useCallback(
        (step: number) => {
            if (value === undefined) setActiveStep(step);

            onValueChange?.(step);
        },
        [value, onValueChange],
    );

    const currentStep = value ?? activeStep;

    // Keyboard navigation logic
    const focusTrigger = React.useCallback(
        (idx: number) => {
            if (triggerNodes[idx]) triggerNodes[idx].focus();
        },
        [triggerNodes],
    );

    const focusNext = React.useCallback(
        (currentIdx: number) => focusTrigger((currentIdx + 1) % triggerNodes.length),
        [focusTrigger, triggerNodes],
    );

    const focusPrev = React.useCallback(
        (currentIdx: number) => focusTrigger((currentIdx - 1 + triggerNodes.length) % triggerNodes.length),
        [focusTrigger, triggerNodes],
    );

    const focusFirst = React.useCallback(
        () => focusTrigger(0),
        [focusTrigger],
    );

    const focusLast = React.useCallback(
        () => focusTrigger(triggerNodes.length - 1),
        [focusTrigger, triggerNodes],
    );

    // Context value
    const contextValue = React.useMemo<StepperContextValue>(
        () => ({
            activeStep: currentStep,
            focusFirst,
            focusLast,
            focusNext,
            focusPrev,
            indicators,
            orientation,
            registerTrigger,
            setActiveStep: handleSetActiveStep,
            stepsCount: React.Children.toArray(children).filter(
                (child): child is React.ReactElement => React.isValidElement(child) && (child.type as { displayName?: string }).displayName === "StepperItem",
            ).length,
            triggerNodes,
        }),
        [
            currentStep,
            handleSetActiveStep,
            children,
            orientation,
            registerTrigger,
            triggerNodes,
            focusFirst,
            focusLast,
            focusNext,
            focusPrev,
            indicators,
        ],
    );

    return (
        <StepperContext.Provider value={contextValue}>
            <div
                aria-orientation={orientation}
                data-orientation={orientation}
                data-slot="stepper"
                role="tablist"
                className={generateValidClassNameHandler(
                    "w-full",
                    className,
                )}
                {...props}
            >
                {children}
            </div>
        </StepperContext.Provider>
    );
};

interface StepperItemProps extends React.HTMLAttributes<HTMLDivElement> {
    completed?: boolean,
    disabled?: boolean,
    loading?: boolean,
    step: number,
}

const StepperItem: React.FC<StepperItemProps> = ({
    children,
    className,
    completed = false,
    disabled = false,
    loading = false,
    step,
    ...props
}) => {
    const { activeStep } = useStepper();

    let state: StepState;

    if (completed || step < activeStep) state = "completed";
    else if (activeStep === step) state = "active";
    else state = "inactive";

    const isLoading = loading && step === activeStep;

    const contextValue = React.useMemo(
        () => ({
            isDisabled: disabled,
            isLoading,
            state,
            step,
        }),
        [
            disabled,
            isLoading,
            state,
            step,
        ],
    );

    return (
        <StepItemContext.Provider value={contextValue}>
            <div
                data-slot="stepper-item"
                data-state={state}
                className={generateValidClassNameHandler(
                    "group/step flex items-center justify-center group-data-[orientation=horizontal]/stepper-nav:flex-row group-data-[orientation=vertical]/stepper-nav:flex-col not-last:flex-1",
                    className,
                )}
                {...(isLoading ? { "data-loading": true } : {})}
                {...props}
            >
                {children}
            </div>
        </StepItemContext.Provider>
    );
};

interface StepperTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> { asChild?: boolean }

const StepperTrigger: React.FC<StepperTriggerProps> = ({
    asChild = false,
    children,
    className,
    tabIndex,
    ...props
}) => {
    const {
        isLoading,
        state,
    } = useStepItem();

    const stepperCtx = useStepper();

    const {
        activeStep,
        focusFirst,
        focusLast,
        focusNext,
        focusPrev,
        registerTrigger,
        setActiveStep,
        triggerNodes,
    } = stepperCtx;

    const {
        isDisabled,
        step,
    } = useStepItem();

    const isSelected = activeStep === step;

    const id = `stepper-tab-${step}`;

    const panelId = `stepper-panel-${step}`;

    // Register this trigger for keyboard navigation
    const btnRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(
        () => {
            if (btnRef.current) registerTrigger(btnRef.current);
        },
        [registerTrigger],
    );

    // Find our index among triggers for navigation
    const myIdx = React.useMemo(
        () => triggerNodes.findIndex((n: HTMLButtonElement) => n === btnRef.current),
        [triggerNodes],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
            e.preventDefault();
            if (myIdx !== -1 && focusNext) focusNext(myIdx);
            break;
        case "ArrowLeft":
        case "ArrowUp":
            e.preventDefault();
            if (myIdx !== -1 && focusPrev) focusPrev(myIdx);
            break;
        case "Home":
            e.preventDefault();
            if (focusFirst) focusFirst();
            break;
        case "End":
            e.preventDefault();
            if (focusLast) focusLast();
            break;
        case "Enter":
        case " ":
            e.preventDefault();
            setActiveStep(step);
            break;
        }
    };

    let computedTabIndex: number;

    if (typeof tabIndex === "number") computedTabIndex = tabIndex;
    else if (isSelected) computedTabIndex = 0;
    else computedTabIndex = -1;

    if (asChild) {
        return (
            <span
                className={className}
                data-slot="stepper-trigger"
                data-state={state}
            >
                {children}
            </span>
        );
    }

    return (
        <button
            aria-controls={panelId}
            aria-selected={isSelected}
            data-loading={isLoading}
            data-slot="stepper-trigger"
            data-state={state}
            disabled={isDisabled}
            id={id}
            ref={btnRef}
            role="tab"
            tabIndex={computedTabIndex}
            className={generateValidClassNameHandler(
                "cursor-pointer focus-visible:border-ring focus-visible:ring-ring/50 inline-flex items-center gap-3 rounded-full outline-none focus-visible:z-10 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-60",
                className,
            )}
            onClick={() => setActiveStep(step)}
            onKeyDown={handleKeyDown}
            {...props}
        >
            {children}
        </button>
    );
};

const StepperIndicator = ({
    children,
    className,
}: React.ComponentProps<"div">) => {
    const {
        isLoading,
        state,
    } = useStepItem();

    const { indicators } = useStepper();

    const getIndicatorContent = () => {
        if (children) return children;

        if (isLoading && indicators.loading) return indicators.loading;

        if (state === "completed") {
            if (indicators.completed) return indicators.completed;

            return (
                <CheckMarkCircle
                    className="bg-success stroke-white"
                    size={30}
                />
            );
        }

        if (state === "active") {
            if (indicators.active) return indicators.active;

            return <div className="size-4 rounded-full border bg-primary ring-1 ring-primary ring-offset-4" />;
        }

        if (state === "inactive") {
            if (indicators.inactive) return indicators.inactive;

            return <div className="size-7 rounded-full border border-gray-300 bg-gray-200" />;
        }

        return null;
    };

    return (
        <div
            data-slot="stepper-indicator"
            data-state={state}
            className={generateValidClassNameHandler(
                "relative flex items-center overflow-hidden justify-center size-7 shrink-0 rounded-full ",
                className,
            )}
        >
            <div className="absolute">
                {getIndicatorContent()}
            </div>
        </div>
    );
};

const StepperSeparator = ({ className }: React.ComponentProps<"div">) => {
    const { state } = useStepItem();

    return (
        <div
            data-slot="stepper-separator"
            data-state={state}
            className={generateValidClassNameHandler(
                "m-0.5 rounded-full bg-muted group-data-[orientation=vertical]/stepper-nav:h-12 group-data-[orientation=vertical]/stepper-nav:w-0.5 group-data-[orientation=horizontal]/stepper-nav:h-0.5 group-data-[orientation=horizontal]/stepper-nav:flex-1",
                className,
            )}
        />
    );
};

const StepperTitle = ({
    children,
    className,
}: React.ComponentProps<"h3">) => {
    const { state } = useStepItem();

    return (
        <h3
            data-slot="stepper-title"
            data-state={state}
            className={generateValidClassNameHandler(
                "text-sm font-medium leading-none",
                className,
            )}
        >
            {children}
        </h3>
    );
};

const StepperDescription = ({
    children,
    className,
}: React.ComponentProps<"div">) => {
    const { state } = useStepItem();

    return (
        <div
            data-slot="stepper-description"
            data-state={state}
            className={generateValidClassNameHandler(
                "text-sm text-muted-foreground",
                className,
            )}
        >
            {children}
        </div>
    );
};

const StepperNav = ({
    children,
    className,
}: React.ComponentProps<"nav">) => {
    const {
        activeStep,
        orientation,
    } = useStepper();

    return (
        <nav
            data-orientation={orientation}
            data-slot="stepper-nav"
            data-state={activeStep}
            className={generateValidClassNameHandler(
                "group/stepper-nav inline-flex data-[orientation=horizontal]:w-full data-[orientation=horizontal]:flex-row data-[orientation=vertical]:flex-col",
                className,
            )}
        >
            {children}
        </nav>
    );
};

const StepperPanel = ({
    children,
    className,
}: React.ComponentProps<"div">) => {
    const { activeStep } = useStepper();

    return (
        <div
            data-slot="stepper-panel"
            data-state={activeStep}
            className={generateValidClassNameHandler(
                "w-full",
                className,
            )}
        >
            {children}
        </div>
    );
};

interface StepperContentProps extends React.ComponentProps<"div"> {
    forceMount?: boolean,
    value: number,
}

const StepperContent = ({
    children,
    className,
    forceMount,
    value,
}: StepperContentProps) => {
    const { activeStep } = useStepper();

    const isActive = value === activeStep;

    if (!forceMount && !isActive) return null;

    return (
        <div
            data-slot="stepper-content"
            data-state={activeStep}
            hidden={!isActive && forceMount}
            className={generateValidClassNameHandler(
                "w-full",
                className,
                !isActive && forceMount && "hidden",
            )}
        >
            {children}
        </div>
    );
};

export {
    Stepper,
    StepperContent,
    type StepperContentProps,
    StepperDescription,
    StepperIndicator,
    StepperItem,
    type StepperItemProps,
    StepperNav,
    StepperPanel,
    type StepperProps,
    StepperSeparator,
    StepperTitle,
    StepperTrigger,
    type StepperTriggerProps,
    useStepItem,
    useStepper,
};
